<template>
    <div id="burofax-list" class="admin-main" v-if="can('burofax.index.admin')">
        <the-nav>
            <h1>Burofaxes</h1>

            <div class="buttons-container">
                <button class="btn btn-danger icon-wrapper"
                        @click="emptyFilters">
                    <i class="uq-icon bi-filter"></i>&nbsp;Limpiar filtros
                </button>
            </div>
        </the-nav>

        <div class="row form-container">
            <div class="col-4">
                <label
                    class="form-label d-flex justify-content-center align-items-center bg-primary w-100 py-1 me-0 mb-0">
                    Tipo burofax:
                </label>
                <v-select
                    class="px-0"
                    v-model="filters.type"
                    :value="filters.type"
                    label="text"
                    :options="burofaxTypes"
                    :reduce="(returnValue) => (returnValue.id).toString()"
                    @option:selected="loadBurofaxes"
                >
                </v-select>
            </div>

            <div class="col-4">
                <div class="form-group search-form">
                    <label
                        class="form-label d-flex justify-content-center align-items-center bg-primary w-100 py-1 me-0 mb-0">
                        Estado:
                    </label>

                    <v-select
                        class="px-0"
                        v-model="filters.status"
                        :value="filters.status"
                        :reduce="(returnValue) => (returnValue.id).toString()"
                        label="text"
                        :options="burofaxStatus"
                        @option:selected="loadBurofaxes"
                    >
                    </v-select>
                </div>
            </div>

            <div class="col-4">
                <div class="form-group search-form">
                    <label
                        class="form-label d-flex justify-content-center align-items-center bg-primary w-100 py-1 me-0 mb-0">
                        Referencia:
                    </label>

                    <input
                        class="px-0 w-100"
                        type="text"
                        aria-label="Referencia"
                        v-model="filters.reference"
                        @input="loadBurofaxes"/>
                </div>
            </div>
        </div>

        <div class="row form-container">
            <div class="col-4">
                <label
                    class="form-label d-flex justify-content-center align-items-center bg-primary w-100 py-1 me-0 mb-0">
                    Nombre cliente:
                </label>

                <v-multi-select-j-q id="selectClients"
                                    ref="multiselectClients"
                                    @update:value="manageMultiSelectUpdate"
                                    :options="nameClients"
                                    parent-id="burofax-list"
                                    placeholder="- Seleccionar -"
                                    :value="filters.client">
                </v-multi-select-j-q>
            </div>

            <div class="col-4">
                <label
                    class="form-label d-flex justify-content-center align-items-center bg-primary w-100 py-1 me-0 mb-0"
                >
                    Nombre entidad:
                </label>

                <v-select
                    class="px-0"
                    v-model="filters.entity"
                    :value="filters.entity"
                    :reduce="(returnValue) => (returnValue.id).toString()"
                    label="text"
                    :options="nameEntities"
                    @option:selected="loadBurofaxes"
                >
                    <template v-slot:no-options>No se han encontrado resultados</template>
                </v-select>
            </div>

            <div class="col-4">
                <label
                    class="form-label d-flex justify-content-center align-items-center bg-primary w-100 py-1 me-0 mb-0">
                    Enviado por:
                </label>

                <v-select
                    class="px-0"
                    v-model="filters.send_by"
                    :value="filters.send_by"
                    :reduce="(returnValue) => (returnValue.id).toString()"
                    label="text"
                    :options="users"
                    @option:selected="loadBurofaxes"
                >
                    <template v-slot:no-options>No se han encontrado resultados</template>
                </v-select>
            </div>
        </div>

        <v-table ref="burofaxesTable"
                 :meta="burofaxes?.meta"
                 :pagination="burofaxes?.links?.pagination ?? []"
                 @pageChanged="pageChanged"
        >
            <template v-slot:header>
                <th>Tipo</th>
                <th>Referencia</th>
                <th>Estado</th>
                <th>Cliente</th>
                <th>Entidad</th>
                <th>Tipo demanda</th>
                <th>Tipo contrato</th>
                <th>Fecha de creación</th>
                <th>Enviado por</th>
                <th class="fit">Acciones</th>
            </template>

            <template v-slot:body>
                <tr v-for="burofax in burofaxes.data">
                    <td>{{ burofax.type ?? '-' }}</td>
                    <td>{{ burofax.reference }}</td>
                    <td>{{ burofax.status }}</td>
                    <td>#{{ burofax.client.code }} {{ burofax.client.name }} {{ burofax.client.surname }}</td>
                    <td>{{ burofax.entity.name }}</td>
                    <td>{{
                            burofax.expedient.formValues.find(object => object.key === 'tipo_demanda')?.value ?? '-'
                        }}
                    </td>
                    <td>{{ burofax.expedient.contract_type_display }}</td>
                    <td>{{ formatDateToHuman(burofax.created_at) }}</td>
                    <td>{{ burofax.sendBy.name }}</td>
                    <td class="fit">
                        <div class="d-flex justify-content-around">
                            <div class="d-flex align-items-center">
                                <button class="btn btn-download icon-wrapper"
                                        title="Descargar burofax"
                                        @click="downloadBurofax(burofax, 'Burofax')">
                                    <i class="uq-icon bi-download"/>
                                </button>
                            </div>

                            <div class="d-flex align-items-center">
                                <button class="btn btn-download icon-wrapper"
                                        title="Descargar acuse de recibo"
                                        @click="downloadBurofax(burofax, 'AcuseRecibo ')">
                                    <i class="uq-icon bi-receipt"/>
                                </button>
                            </div>

                            <div class="d-flex align-items-center">
                                <button class="btn btn-download icon-wrapper"
                                        title="Actualizar información"
                                        :disabled="updatingBurofax"
                                        @click="updateBurofaxStatus(burofax)">
                                    <i class="uq-icon d-inline-block bi-arrow-clockwise"
                                       :class="{rotate: updatingBurofax}"/>
                                </button>
                            </div>
                        </div>
                    </td>
                </tr>
            </template>
        </v-table>
    </div>
</template>

<script>

import { can } from "../../helpers/Permissions";
import { getAllPaginatedBurofaxes, getBurofax, } from "../../helpers/requests/RequestsBurofaxes";
import VTable from "../general/VTable";
import { formatDateToHuman } from "../../helpers/DateHelper";
import router from "../../router";
import { filtersToQueryParameters, setQueryParameters } from "../../helpers/Url";
import TheNav from "../layouts/TheNav.vue";
import vSelect from 'vue-select';
import { getAllClientsWithoutPagination } from "../../helpers/requests/RequestsClients";
import { getUsers } from "../../helpers/requests/RequestsUsers";
import { getEntities } from "../../helpers/requests/RequestsEntities";
import VMultiSelectJQ from "../general/VMultiSelectJQ.vue";

export default {
    name: "BurofaxList",
    components: { VMultiSelectJQ, vSelect, TheNav, VTable },
    data() {
        return {
            burofaxes: {},
            loaded: false,
            updatingBurofax: false,

            filters: {
                type: '',
                reference: '',
                status: '',
                client: '',
                entity: '',
                expedient_id: '',
                date: '',
                send_by: '',
                page: 1,
            },

            availableClients: '',
            codeClients: '',
            nameClients: '',
            nameEntities: '',
            users: '',
        }
    },
    setup() {
        return {
            can,
        }
    },
    computed: {
        burofaxTypes() {
            return [
                { text: 'Burofax Electronico', id: 'Burofax Electronico' },
                { text: 'Burofax Postal', id: 'Burofax Postal' },
                { text: 'Comunicado Electronico', id: 'Comunicado Electronico' },
            ];
        },
        burofaxStatus() {
            return [
                { text: 'Enviado', id: 'SENT' },
                { text: 'Comunicación emitida', id: 'COMUNICATION_SENT' },
                { text: 'Comunicación entregada', id: 'COMUNICATION_GIVEN' },
                { text: 'Comunicación tramitada', id: 'COMUNICATION_TRAMITED' },
                { text: 'Notificación emitida', id: 'NOTIFICATION_SENT' },
                { text: 'Notificación entregada', id: 'NOTIFICATION_GIVEN' },
                { text: 'Notificación tramitada', id: 'NOTIFICATION_TRAMITED' },
                { text: 'Ausente', id: 'ABSENT' },
                { text: 'Reparto', id: 'DISTRIBUTION' },
                { text: 'Direccion Incorrecta', id: 'WRONG_ADDRESS' },
                { text: 'Desconocida', id: 'UNKNOWN', },
                { text: 'No se hace cargo', id: 'DOES_NO_TAKE_CARE' },
                { text: 'Rehusado', id: 'DECLINED' },
                { text: 'Fallecido/a', id: 'DECEASED' },
                { text: 'Pendiete de Recogida en Oficina', id: 'OFFICE_COLLECTION_PENDING' },
                { text: 'No retirado de Oficina', id: 'NOT_RETIRED_FROM_OFFICE' },
                { text: 'Incidencia', id: 'INCIDENCE' },
                { text: 'Destruido', id: 'DESTROYED' },
                { text: 'Entregado', id: 'RECEIVED' },
                { text: 'Finalizado', id: 'FINALIZED' },
            ]
        }
    },
    async mounted() {

        await Promise.all([
            this.loadAvailableClients(),
            this.loadEntities(),
            this.loadUsers(),
            this.loadBurofaxes(),
        ]);
    },
    methods: {
        manageMultiSelectUpdate(event) {
            this.filters.client = event;

            this.loadBurofaxes();
        },
        formatDateToHuman,
        async loadBurofaxes(isFilter = true) {
            this.loaded = false;

            if (isFilter && this.filters.page !== 1) {
                this.filters.page = 1
            }

            await this.setFiltersAsQueryParameters();
            let query = filtersToQueryParameters(this.filters);
            this.burofaxes = await getAllPaginatedBurofaxes(query);
            this.loaded = true;
        },
        emptyFilters() {
            this.filters = {
                type: '',
                reference: '',
                status: '',
                client: '',
                expedient_id: '',
                date: '',
                send_by: '',
                page: 1,
            };

            this.loadBurofaxes(false);
        },
        setQueryParametersAsFilters() {
            let query_parameters = router.currentRoute.value.query;

            console.log(query_parameters)

            if (query_parameters.type) {
                this.filters.type = query_parameters.type;
            } else {
                this.filters.type = '';
            }

            if (query_parameters.reference) {
                this.filters.reference = query_parameters.reference;
            } else {
                this.filters.reference = '';
            }

            if (query_parameters.status) {
                this.filters.status = query_parameters.status;
            } else {
                this.filters.status = '';
            }

            if (query_parameters.client) {
                this.filters.client = query_parameters.client;
            } else {
                this.filters.client = '';
            }

            if (query_parameters.entity) {
                this.filters.entity = query_parameters.entity;
            } else {
                this.filters.entity = '';
            }

            if (query_parameters.expedient_id) {
                this.filters.expedient_id = query_parameters.expedient_id;
            } else {
                this.filters.expedient_id = '';
            }

            if (query_parameters.date) {
                this.filters.date = query_parameters.date;
            } else {
                this.filters.date = '';
            }

            if (query_parameters.send_by) {
                this.filters.send_by = query_parameters.send_by;
            } else {
                this.filters.send_by = '';
            }

            if (query_parameters.page) {
                this.filters.page = query_parameters.page;
            } else {
                this.filters.page = 1;
            }
        },
        setFiltersAsQueryParameters() {
            setQueryParameters('burofax.index', this.filters);
        },
        async updateBurofaxStatus(burofax) {
            try {
                this.updatingBurofax = true;

                await getBurofax(burofax.id);
            } catch (e) {
                if (!!e?.response?.data?.message) {
                    alert(e.response.data.message);
                } else {
                    alert('Ha ocurrido un error al actualizar el burofax');
                }
            } finally {
                this.updatingBurofax = false;
                await this.loadBurofaxes();
                this.$emit('updateExpedient');
            }
        },
        async downloadBurofax(burofax, type) {
            try {
                const response = await axios.get(`/api/burofax/${burofax.id}/download/${type}`, {
                    responseType: 'blob',
                });

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                const name = (type === 'Burofax' ? 'BU_' : 'AC_')
                    + (burofax.reference.toString() ?? 'NO_REFERENCE')
                    + '.pdf';

                link.setAttribute('download', name);
                document.body.appendChild(link);
                link.click();
            } catch (e) {
                if (!!e?.response?.data?.message) {
                    alert(e.response.data.message);
                } else {
                    alert('Ha ocurrido un error al descargar el burofax');
                }
            }
        },
        async loadAvailableClients() {
            let clients = await getAllClientsWithoutPagination();

            if (!clients.meta) {
                this.nameClients = Object.values(clients).map((client) => {
                    return { id: client.id, text: '#' + client.code + ' ' + client.fullName }
                });
            }
        },
        async loadEntities() {
            let entities = await getEntities();

            this.nameEntities = Object.values(entities).map((entity) => {
                return { id: entity.id, text: entity.name }
            });
        },
        async loadUsers() {
            const raw_users = await getUsers();

            this.users = Object.values(raw_users).map((user) => {
                return { id: user.id, text: user.name }
            });
        },
        async pageChanged(page) {
            this.filters.page = page;
            await this.loadBurofaxes(false);
        },
    },
}

</script>
